import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { CheckboxField } from '@/components/form/checkbox/Checkbox';
import { TsForm } from '@/components/form/Form';
import { NumberField } from '@/components/form/NumberInput';
import { SubmitCancelButtons } from '@/components/form/SubmitCancelButtons';
import { TextField } from '@/components/form/TextInput';
import { TimeField } from '@/components/form/TimeInput';
import { TSTitleCard } from '@/components/shared/card/Card';
import { secondsSinceMidnightToTimeString, timeStringToSecondsSinceMidnight } from '@/helpers/dateHelper';
import { handleMutationResult } from '@/helpers/toastHelper';
import { Contract } from '@/services/contract/contracts.types';
import { useContracts } from '@/services/contract/useContracts';
import { useUpdateContractMutation } from '@/services/gql/graphql.generated';
import { strings } from '@/services/translation/strings';
import { FunctionComponent, useEffect } from 'react';
import { Path, useForm } from 'react-hook-form';
import { FiFileText } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import adminStrings from '../../admin.translations';

interface AdminEditContractDetailsCardProps {
  contract: Contract;
}

interface EditContractFormValues {
  name: string;
  earliestStartTime: string;
  latestEndTime: string;
  minHoursPerWeek: number;
  maxHoursPerWeek: number;
  minHoursPerDay: number;
  maxHoursPerDay: number;
  maxDaysPerWeek: number;
  maxConsecDays: number;
  maxHoursPerShift: number;
  allowOvertime: boolean;
}

const field = (name: Path<EditContractFormValues>) => name;

const EDIT_STRINGS = adminStrings.contractsSection.edit;
const MAX_LOWER_THAN_MIN = EDIT_STRINGS.maxCannotBeLowerThanMin;
const NOT_ENFORCED_TOOLTIP = EDIT_STRINGS.notEnforcedTooltip;
const TIME_NOT_ENFORCED_TOOLTIP = EDIT_STRINGS.notEnforcedTimeTooltip;

const AdminEditContractDetailsCard: FunctionComponent<AdminEditContractDetailsCardProps> = ({ contract }) => {
  const navigate = useNavigate();

  const methods = useForm<EditContractFormValues>({
    defaultValues: {
      name: contract.name,
      earliestStartTime: secondsSinceMidnightToTimeString(contract.earliestStartTime ?? 0),
      latestEndTime: secondsSinceMidnightToTimeString(contract.latestEndTime ?? 0),
      minHoursPerWeek: contract.minHoursPerWeek,
      maxHoursPerWeek: contract.maxHoursPerWeek,
      minHoursPerDay: contract.minHoursPerDay,
      maxHoursPerDay: contract.maxHoursPerDay,
      maxDaysPerWeek: contract.maxDaysPerWeek,
      maxConsecDays: contract.maxConsecutiveDays,
      maxHoursPerShift: contract.maxHoursPerShift,
      allowOvertime: contract.overtimeAllowed
    }
  });

  useEffect(() => {
    methods.reset();
  }, [contract]);

  // Re-trigger the paired field's validation after a submit attempt so errors
  // clear immediately when the user corrects the min/start value.
  const earliestStartTime = methods.watch('earliestStartTime');
  const minHoursPerWeek = methods.watch('minHoursPerWeek');
  const minHoursPerDay = methods.watch('minHoursPerDay');

  useEffect(() => {
    if (methods.formState.isSubmitted) methods.trigger('latestEndTime');
  }, [earliestStartTime]);

  useEffect(() => {
    if (methods.formState.isSubmitted) methods.trigger('maxHoursPerWeek');
  }, [minHoursPerWeek]);

  useEffect(() => {
    if (methods.formState.isSubmitted) methods.trigger('maxHoursPerDay');
  }, [minHoursPerDay]);

  const [{ fetching }, update] = useUpdateContractMutation();

  const { contracts } = useContracts();

  const { refetch } = useContracts();

  const onSubmit = async (values: EditContractFormValues) => {
    const result = await update({
      contract: {
        id: contract.id,
        name: values.name,
        earliestStartTime: timeStringToSecondsSinceMidnight(values.earliestStartTime),
        latestEndTime: timeStringToSecondsSinceMidnight(values.latestEndTime),
        minHoursPerWeek: values.minHoursPerWeek,
        maxHoursPerWeek: values.maxHoursPerWeek,
        minHoursPerDay: values.minHoursPerDay,
        maxHoursPerDay: values.maxHoursPerDay,
        maxDaysPerWeek: values.maxDaysPerWeek,
        maxConsecutiveDays: values.maxConsecDays,
        maxHoursPerShift: values.maxHoursPerShift,
        overtimeAllowed: values.allowOvertime
      }
    });

    await refetch({ requestPolicy: 'network-only' });
    setTimeout(
      () =>
        handleMutationResult(result, {
          successMessage: adminStrings.tasksSection.edit.successMessage(values.name),
          errorMessage: adminStrings.tasksSection.edit.errorMessage(contract.name ?? ''),
          customSuccessCheck: data => !!data?.updateContract,
          onSuccess: handleClose
        }),
      100
    );
  };

  const handleClose = () => {
    methods.reset();
    navigate('/admin/contracts');
  };

  return (
    <TSTitleCard title={EDIT_STRINGS.title(contract.name ?? '')} icon={FiFileText} className="w-full">
      <SubmissionErrorBoundary>
        <TsForm methods={methods} onSubmit={onSubmit}>
          <div className="flex flex-col space-y-3 w-full">
            <TextField
              label={EDIT_STRINGS.contractName}
              name={field('name')}
              placeholder={EDIT_STRINGS.contractName}
              customValidatorMessage={methods.formState.errors.name?.message}
              required
              rules={{
                validate: (value: string) => {
                  const isDuplicate = contracts?.some(c => c?.name === value && c?.id !== contract.id);
                  if (isDuplicate) return EDIT_STRINGS.uniqueName;
                  return true;
                }
              }}
            />

            <div className="flex flex-row space-x-5">
              <TimeField
                className="w-72 h-fit"
                name={field('earliestStartTime')}
                label={EDIT_STRINGS.earliestStartTime}
                tooltipText={TIME_NOT_ENFORCED_TOOLTIP}
                optional
              />
              <TimeField
                className="w-72 h-fit"
                name={field('latestEndTime')}
                label={EDIT_STRINGS.latestEndTime}
                tooltipText={TIME_NOT_ENFORCED_TOOLTIP}
                optional
                rules={{
                  validate: (end: string) => {
                    const start = methods.getValues('earliestStartTime');
                    if (!start || start === '00:00') return true;
                    return end >= start || MAX_LOWER_THAN_MIN;
                  }
                }}
              />
            </div>

            <div className="flex flex-row space-x-5">
              <NumberField
                className="w-72 h-fit"
                name={field('minHoursPerWeek')}
                label={EDIT_STRINGS.minHoursPerWeek}
                tooltipText={NOT_ENFORCED_TOOLTIP}
                optional
              />
              <NumberField
                className="w-72 h-fit"
                name={field('maxHoursPerWeek')}
                label={EDIT_STRINGS.maxHoursPerWeek}
                tooltipText={NOT_ENFORCED_TOOLTIP}
                optional
                rules={{
                  validate: (max: number) => {
                    const min = methods.getValues('minHoursPerWeek');
                    if (!min || !max) return true;
                    return max >= min || MAX_LOWER_THAN_MIN;
                  }
                }}
              />
            </div>

            <div className="flex flex-row space-x-5">
              <NumberField
                className="w-72 h-fit"
                name={field('minHoursPerDay')}
                label={EDIT_STRINGS.minHoursPerDay}
                tooltipText={NOT_ENFORCED_TOOLTIP}
                optional
              />
              <NumberField
                className="w-72 h-fit"
                name={field('maxHoursPerDay')}
                label={EDIT_STRINGS.maxHoursPerDay}
                tooltipText={NOT_ENFORCED_TOOLTIP}
                optional
                rules={{
                  validate: (max: number) => {
                    const min = methods.getValues('minHoursPerDay');
                    if (!min || !max) return true;
                    return max >= min || MAX_LOWER_THAN_MIN;
                  }
                }}
              />
            </div>

            <NumberField
              name={field('maxDaysPerWeek')}
              label={EDIT_STRINGS.maxDaysPerWeek}
              tooltipText={NOT_ENFORCED_TOOLTIP}
              optional
            />

            <NumberField
              name={field('maxConsecDays')}
              label={EDIT_STRINGS.maxConsecutiveDays}
              tooltipText={NOT_ENFORCED_TOOLTIP}
              optional
            />

            <NumberField
              name={field('maxHoursPerShift')}
              label={EDIT_STRINGS.maxHoursPerShift}
              tooltipText={NOT_ENFORCED_TOOLTIP}
              optional
            />

            <CheckboxField name={field('allowOvertime')} label={EDIT_STRINGS.allowOvertime} />

            <div className="flex items-center justify-end gap-3">
              <SubmitCancelButtons
                disabled={!!methods.formState.errors.name?.message}
                onComplete={handleClose}
                loading={fetching}
                submitText={strings.common.save}
              />
            </div>
          </div>
        </TsForm>
      </SubmissionErrorBoundary>
    </TSTitleCard>
  );
};

export default AdminEditContractDetailsCard;
